import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputComponent from '../../components/InputMC';
import RangeInputMC from '../../components/RangeInputMC';
import ToggleSwitchMC from '../../components/ToggleSwitchMC';
import ButtonComponent from '../../components/ButtonMC';
import { uuid } from '../../../util/utils';
import TitleDescriptionMC from '../../components/TitleDescriptionMC';

const CreateFunnelStepOpened = ({
  onChange,
  data,
  selectedItem,
  closeAction
}) => {
  const [name, setName] = useState(selectedItem.name || '');
  const [probability, setProbability] = useState(selectedItem.probability || 0);
  const [stagnationTimeEnabled, setStagnationTimeEnabled] = useState(selectedItem.stagnationTimeEnabled || false);
  const [stagnationDays, setStagnationDays] = useState(selectedItem.stagnationDays || '');
  const [customError, setCustomError] = useState(null);
  const [stagnationTimeError, setStagnationTimeError] = useState(null);

  const isNew = !selectedItem.id;
  const id = useMemo(() => selectedItem.id || uuid(), [selectedItem.id]);

  const { t } = useTranslation('Funnels');

  const onSave = () => {
    const allSteps = [...data.openSteps, ...data.closedSteps];
    const nameExists = allSteps.some((step) => step.name === name && step.id !== selectedItem.id);

    if (nameExists) {
      setCustomError(t('sidebar.createFromZero.errors.nameExists'));
      return;
    }

    // Stagnation time validation
    if (stagnationTimeEnabled) {
      if (
        stagnationDays === '' ||
        !/^\d+$/.test(stagnationDays) ||
        stagnationDays.length > 10
      ) {
        setStagnationTimeError(t('sidebar.createFromZero.errors.stagnationTime'));
        return;
      }
    }

    setCustomError(null);
    setStagnationTimeError(null)

    const newItem = {
      id,
      name,
      probability,
      stagnationTimeEnabled,
      stagnationDays
    };

    const updatedSteps = isNew
      ? [...data.openSteps, newItem]
      : data.openSteps.map((step) => (step.id === selectedItem.id ? newItem : step));
    onChange('openSteps', updatedSteps);
    closeAction();
  };

  const submitDisabled = name.trim().length === 0;

  return (
    <div className="tw-wrapper">
      {!isNew && (
        <p className="tw-text-xs tw-pb-7">{t('sidebar.createFromZero.stageOpen.description')}</p>
      )}
      <InputComponent
        value={name}
        onChange={(key, value) => setName(value)}
        name={`step_name_${id}`}
        label={t('sidebar.createFromZero.stageOpen.fields.name.inputLabel')}
        placeholder={t('sidebar.createFromZero.stageOpen.fields.name.placeholder')}
        className="tw-resize-none"
        useFloatingLabel
        customError={customError}
      />
      <TitleDescriptionMC
        title={t('sidebar.createFromZero.stageOpen.fields.probability.title')}
        description={t('sidebar.createFromZero.stageOpen.fields.probability.subtitle')}
        className="tw-mt-4"
      />
      <RangeInputMC
        value={probability}
        min={0}
        max={100}
        className="tw-mt-4 tw-w-96 tw-mb-6"
        onChange={({ target }) => setProbability(target.value)}
      />
      <ToggleSwitchMC
        isOn={stagnationTimeEnabled}
        handleToggle={() => {
          if (stagnationTimeEnabled) {
            // Turning OFF: clear the value and error
            setStagnationDays('');
            setStagnationTimeError(null);
          }
          setStagnationTimeEnabled(!stagnationTimeEnabled);
        }}
        label={t('sidebar.createFromZero.stageOpen.fields.stagnationTime.label')}
        labelClassName="!tw-text-base !tw-font-semibold tw-font-sans"
      />
      <p className="tw-text-xs tw-pb-2">
        {t('sidebar.createFromZero.stageOpen.fields.stagnationTime.description')}
      </p>

      {stagnationTimeEnabled && (
        <InputComponent
          value={stagnationDays}
          onChange={(key, value) => setStagnationDays(value)}
          name={`days_${id}`}
          type="number"
          label={t('sidebar.createFromZero.stageOpen.fields.stagnationTime.inputLabel')}
          placeholder={t('sidebar.createFromZero.stageOpen.fields.stagnationTime.placeholder')}
          className="tw-resize-none"
          useFloatingLabel
          customError={stagnationTimeError}
        />
      )}

      <ButtonComponent
        text={
          isNew
            ? t('sidebar.createFromZero.buttons.stepNew')
            : t('sidebar.createFromZero.buttons.stepEdit')
        }
        className="tw-w-full tw-py-3 tw-mt-6 tw-text-sm"
        disabled={submitDisabled}
        onClick={onSave}
      />
      <ButtonComponent
        text={t('sidebar.createFromZero.buttons.cancel')}
        className="tw-w-full tw-py-3 tw-text-sm"
        variant="SECONDARY"
        onClick={closeAction}
      />
    </div>
  );
};

export default CreateFunnelStepOpened;
